import * as React from 'react';
import styled from 'styled-components';
import { Icon, IconSizeType, FontSize } from '@openfin/ui-library';

export interface AppLogoProps {
  src: string;
  /**
   * Alt text for image.
   * If the image failed to load the first character of the alt text will be used as an icon.
   */
  alt: string;
  /**
   * Unique key for generating fallback background color.
   * If no `uuid` is defined `src` is used.
   */
  uuid?: string;
  size?: IconSizeType;
  tooltip?: boolean;
  className?: string;
}

export const AppLogo = ({
  src,
  alt,
  uuid = src,
  tooltip,
  size,
  className,
}: AppLogoProps): JSX.Element => {
  const imageState = useLoaded(src);
  const initials = alt?.substr(0, 1);
  const hasImg = !!src;
  const hasImgNotFailing = hasImg && imageState !== LoadState.ERROR;
  const title = !hasImgNotFailing && tooltip ? alt : undefined;
  const backgroundColor = !hasImgNotFailing ? generateHslColor(uuid) : 'transparent';

  return (
    <LogoIcon
      src={src}
      size={size}
      className={className}
      backgroundColor={backgroundColor}
      title={title}
    >
      {!hasImgNotFailing && initials}
    </LogoIcon>
  );
};

type LogoIconProps = Omit<AppLogoProps, 'alt'> & { backgroundColor: string };

const LogoIcon = styled(Icon)<LogoIconProps>`
  flex-shrink: 0;
  user-select: none;
  border-radius: ${({ theme }) => theme.radius.small};
  background-image: url(${({ src }) => src});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${({ backgroundColor }) => backgroundColor};
  margin-right: ${({ theme }) => theme.px.small};
  text-transform: uppercase;
  text-align: center;
  font-weight: bold;
  font-size: ${({ size }) => FontSize[size as keyof typeof FontSize]};
  place-content: center;
  place-items: center;
`;

export enum LoadState {
  LOADING = 'loading',
  ERROR = 'error',
  LOADED = 'loaded',
}

const useLoaded = (src = '', srcSet?: string) => {
  const [loaded, setLoaded] = React.useState<LoadState>(LoadState.LOADING);

  React.useEffect(() => {
    if (!src && !srcSet) {
      return;
    }
    const image = new Image();

    let active = true;
    image.onload = () => active && setLoaded(LoadState.LOADED);
    image.onerror = () => active && setLoaded(LoadState.ERROR);

    image.src = src;
    if (srcSet) {
      image.srcset = srcSet;
    }

    () => {
      active = false;
    };
  }, [src, srcSet]);

  return loaded;
};

function generateHslColor(str: string, saturation = 70, lightness = 50) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const hue = hash % 360;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}
